import {
  Box,
  CircularProgress,
  Typography,
  useTheme,
} from '@mui/material';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js';
import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { EmptyState } from '../../../components/EmptyState';
import * as styles from './ArtistsChart.styles';
import { ArtistData } from './ArtistsTable';
import { CHART_DATASET_COLORS } from './chartColors';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
);

export interface ArtistsChartProps {
  data: ArtistData[];
  loading: boolean;
  error: string | null;
}

export const ArtistsChart: React.FC<ArtistsChartProps> = ({
  data,
  loading,
  error,
}) => {
  const theme = useTheme();

  // Use real data - backend already returns top 10 sorted by total engagements
  const chartData = useMemo(() => {
    // Sort by total popularity (plays + adds + shares) descending to ensure proper order
    const sorted = [...data].sort(
      (a, b) => b.plays + b.adds + b.shares - (a.plays + a.adds + a.shares),
    );
    
    // Take top 10 (backend already limits, but this ensures we don't exceed)
    const top10 = sorted.slice(0, 10);

    return {
      labels: top10.map((item) => item.name),
      datasets: [
        {
          label: 'Plays',
          data: top10.map((item) => item.plays),
          ...CHART_DATASET_COLORS.plays,
        },
        {
          label: 'Adds',
          data: top10.map((item) => item.adds),
          ...CHART_DATASET_COLORS.adds,
        },
        {
          label: 'Shares',
          data: top10.map((item) => item.shares),
          ...CHART_DATASET_COLORS.shares,
        },
      ],
    };
  }, [data]);

  const chartOptions = useMemo(
    () => {
      // Capture labels for use in callback
      const labels = chartData.labels;
      
      return {
        indexAxis: 'y' as const,
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index' as const,
          intersect: true,
          axis: 'y' as const,
        },
        plugins: {
          legend: {
            display: true,
            position: 'top' as const,
            labels: {
              usePointStyle: true,
              padding: 15,
              font: {
                size: 12,
                family: theme.typography.fontFamily,
              },
              color: theme.palette.text.primary,
            },
          },
          tooltip: {
            mode: 'index' as const,
            intersect: true,
            backgroundColor:
              theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.9)' : '#fff',
            titleColor: theme.palette.text.primary,
            bodyColor: theme.palette.text.primary,
            borderColor: theme.palette.divider,
            borderWidth: 1,
            padding: 12,
            displayColors: true,
            usePointStyle: true,
            filter: () => true, // Show all items even if value is 0
            position: (context: any) => {
              // Position tooltip at cursor location
              // Use default 'nearest' positioning which follows cursor
              // Custom coordinate positioning can cause issues with Chart.js internal state
              return 'nearest';
            },
            itemSort: (a: any, b: any) => {
              // Sort order: Plays, Adds, Shares
              const order = ['Plays', 'Adds', 'Shares'];
              const aIndex = order.indexOf(a.dataset.label || '');
              const bIndex = order.indexOf(b.dataset.label || '');
              return aIndex - bIndex;
            },
            callbacks: {
              title: (tooltipItems: any[]) => {
                return tooltipItems[0].label;
              },
              label: (context: any) => {
                const label = context.dataset.label || '';
                const value = context.parsed.x || 0;
                return `${label}: ${value.toLocaleString()}`;
              },
            },
          },
        },
        scales: {
          x: {
            stacked: true,
            beginAtZero: true,
            ticks: {
              color: theme.palette.text.primary,
              font: {
                size: 12,
                family: theme.typography.fontFamily,
              },
              callback: function (value: string | number) {
                const numValue = typeof value === 'number' ? value : Number(value);
                if (numValue >= 1000) {
                  return (numValue / 1000).toFixed(1) + 'k';
                }
                return value;
              },
            },
            grid: {
              color: theme.palette.divider,
            },
            title: {
              display: true,
              text: 'Popularity',
              color: theme.palette.text.primary,
              font: {
                size: 12,
                family: theme.typography.fontFamily,
              },
            },
          },
          y: {
            stacked: true,
            ticks: {
              color: theme.palette.text.primary,
              font: {
                size: 12,
                family: theme.typography.fontFamily,
              },
              padding: 8,
              callback: function (value: string | number, index: number) {
                // Use captured labels array to truncate display
                if (labels && index >= 0 && index < labels.length) {
                  const label = labels[index] as string;
                  if (label && typeof label === 'string' && label.length > 20) {
                    return label.substring(0, 20) + '...';
                  }
                  return label || '';
                }
                return '';
              },
            },
          grid: {
            display: false,
          },
          title: {
            display: true,
            text: 'Artist Name',
            color: theme.palette.text.primary,
            font: {
              size: 12,
              family: theme.typography.fontFamily,
            },
            padding: {
              left: 10,
              right: 0,
              top: 0,
              bottom: 0,
            },
          },
          afterFit: (scaleInstance: any) => {
            // Set fixed width to ensure consistent bar positioning across all charts
            scaleInstance.width = 200;
          },
        },
      },
      };
    },
    [theme, chartData],
  );

  if (loading) {
    return (
      <Box sx={styles.chartContainer}>
        <Box sx={styles.loadingContainer}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={styles.chartContainer}>
        <Box sx={styles.errorContainer}>
          <Typography color="error">{error}</Typography>
        </Box>
      </Box>
    );
  }

  if (data.length === 0) {
    return (
      <Box sx={styles.chartContainer}>
        <EmptyState message="Data coming soon..." />
      </Box>
    );
  }

  return (
    <Box sx={styles.chartContainer}>
      <Box sx={styles.chartWrapper}>
        <Bar data={chartData} options={chartOptions} />
      </Box>
    </Box>
  );
};

